'use client'

import { useEffect, useMemo, useState } from 'react'
import { ExperienceProviders, TrackPage } from '@ccpgames/eve-web-contentful'
import type {
  NinetailedConfig,
  NinetailedPreviewPluginOptions,
  ExperienceConfiguration,
  Reference,
} from '@ccpgames/eve-web-contentful'
import { usePathname } from 'next/navigation'

interface Props {
  children: React.ReactNode
  locale: string
}

const ninetailedClientId = process.env.NEXT_PUBLIC_NINETAILED_CLIENT_ID || ''
const ninetailedEnvironment = process.env.NEXT_PUBLIC_NINETAILED_ENVIRONMENT || 'main'

export default function ExperienceProvidersWrapper({ children, locale }: Props) {
  const [experiencesData, setExperiencesData] = useState<ExperienceConfiguration<Reference>[]>([])
  const [audiencesData, setAudiencesData] = useState<NinetailedPreviewPluginOptions['audiences']>([])

  // Check preview status from server
  const [enablePreview, setEnablePreview] = useState(false)
  const [previewStatusLoaded, setPreviewStatusLoaded] = useState(false)

  useEffect(() => {
    let isActive = true
    const readNtDebugCookie = () => {
      if (typeof document === 'undefined') return false
      const cookies = document.cookie ? document.cookie.split('; ') : []
      for (const cookie of cookies) {
        const [name, value] = cookie.split('=')
        if (name === 'nt_debug') {
          return value ? decodeURIComponent(value) === '1' : false
        }
      }
      return false
    }

    const enabled = readNtDebugCookie()
    if (!isActive) return
    setEnablePreview(enabled)
    setPreviewStatusLoaded(true)

    return () => {
      isActive = false
    }
  }, [])

  // Load data when preview status is determined
  useEffect(() => {
    if (!previewStatusLoaded) {
      return // Wait for preview status to be loaded
    }

    if (!enablePreview) {
      return
    }

    let isActive = true
    const controller = new AbortController()

    const loadData = async () => {
      try {
        const response = await fetch('/api/content/personalizations', { signal: controller.signal })

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`)
        }

        const data = await response.json()

        if (!isActive) return
        setExperiencesData((data.experiences || []) as ExperienceConfiguration<Reference>[])
        setAudiencesData((data.audiences || []) as NinetailedPreviewPluginOptions['audiences'])
      } catch {
        // Swallow; preview will remain undefined
      }
    }

    loadData()

    return () => {
      isActive = false
      controller.abort()
    }
  }, [enablePreview, previewStatusLoaded])

  const previewData = useMemo<NinetailedPreviewPluginOptions | undefined>(() => {
    if (!enablePreview) return undefined
    const hasPreview = (experiencesData?.length ?? 0) > 0 || (audiencesData?.length ?? 0) > 0
    return hasPreview ? { experiences: experiencesData, audiences: audiencesData } : undefined
  }, [enablePreview, experiencesData, audiencesData])

  const ninetailedConfig = useMemo<NinetailedConfig>(() => {
    const base = {
      clientId: ninetailedClientId,
      environment: ninetailedEnvironment,
    }
    return previewData ? { ...base, preview: previewData } : base
  }, [previewData])

  // Always pass locale so Contentful Live Preview uses the current page locale (works for all locales in draft/preview).
  const contentfulLivePreviewConfig = useMemo(() => ({ locale }), [locale])

  const pathname = usePathname()
  const key = pathname?.split('/').pop() || pathname

  return (
    <ExperienceProviders ninetailed={ninetailedConfig} contentfulLivePreview={contentfulLivePreviewConfig}>
      <TrackPage pathname={pathname} />
      <div key={key}>{children}</div>
    </ExperienceProviders>
  )
}
